import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth0 as useAuth0SDK } from '@auth0/auth0-react';
import logger from '../../utils/logger';

interface Auth0ContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: any;
    loginWithRedirect: () => Promise<void>;
    logout: (explicitIntent?: boolean) => void;
    getAccessTokenSilently: (options?: any) => Promise<string>;
    error: Error | undefined;
}

const Auth0Context = createContext<Auth0ContextType | undefined>(undefined);

export const Auth0Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const {
        isAuthenticated,
        isLoading,
        user,
        loginWithRedirect,
        logout: auth0Logout,
        getAccessTokenSilently,
        error
    } = useAuth0SDK();

    // Application-level session management
    const [lastAuthTime, setLastAuthTime] = useState<number | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);

    // 🔒 CRITICAL: Pre-emptive disconnect protection for Coinbase Wallet page reloads
    const [disconnectProtectionActive, setDisconnectProtectionActive] = useState<boolean>(() => {
        // Check for protection flag on initialization (before Auth0 validates session)
        const protectionFlag = localStorage.getItem('wallet_disconnect_in_progress');
        if (protectionFlag) {
            const disconnectTime = parseInt(protectionFlag, 10);
            const now = Date.now();
            const timeSinceDisconnect = now - disconnectTime;

            // If disconnect happened within last 10 seconds, activate protection
            if (timeSinceDisconnect < 10000) {
                logger.log(`🛡️ PRE-EMPTIVE PROTECTION: Wallet disconnect detected on app load (${timeSinceDisconnect}ms ago) - blocking logout for 10 seconds`);
                return true;
            } else {
                // Old flag, clean it up
                localStorage.removeItem('wallet_disconnect_in_progress');
                return false;
            }
        }
        return false;
    });

    // Clear protection after timeout
    useEffect(() => {
        if (disconnectProtectionActive) {
            const timer = setTimeout(() => {
                logger.log('🛡️ PRE-EMPTIVE PROTECTION: Timeout expired, clearing protection');
                setDisconnectProtectionActive(false);
                localStorage.removeItem('wallet_disconnect_in_progress');
            }, 10000); // 10 second protection window

            return () => clearTimeout(timer);
        }
    }, [disconnectProtectionActive]);

    // Update last authentication time and create session ID when user authenticates
    useEffect(() => {
        if (isAuthenticated && !lastAuthTime) {
            setLastAuthTime(Date.now());

            // Generate new session ID on each login for complete session isolation
            const newSessionId = crypto.randomUUID();
            setSessionId(newSessionId);
            sessionStorage.setItem('app_session_id', newSessionId);

            logger.log(`New session created: ${newSessionId}`);
        } else if (!isAuthenticated) {
            setLastAuthTime(null);
            setSessionId(null);
            sessionStorage.removeItem('app_session_id');
        }
    }, [isAuthenticated, lastAuthTime]);

    // Validate session on app initialization
    useEffect(() => {
        if (isAuthenticated && sessionId) {
            const storedSessionId = sessionStorage.getItem('app_session_id');
            if (!storedSessionId || storedSessionId !== sessionId) {
                logger.log('Session validation failed - forcing logout');
                auth0Logout();
            }
        }
    }, [isAuthenticated, sessionId, auth0Logout]);

    // Log authentication state changes in development
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            logger.log('Auth0 state:', {
                isAuthenticated,
                isLoading,
                user: user ? `${user.name} (${user.email})` : 'none',
                error: error ? error.message : null
            });
        }
    }, [isAuthenticated, isLoading, user, error]);

    // 🔒 CRITICAL: Enhanced logout with explicit intent requirement
    // This function now REQUIRES explicit user intent to logout
    // Without the intent flag, it will NOT logout (prevents Coinbase Wallet from triggering logout)
    const logout = useCallback((explicitIntent: boolean = false) => {
        try {
            // 🔒 CRITICAL FIX #0: Check explicit intent FIRST (allow intentional logout button clicks)
            if (explicitIntent) {
                logger.log('✅ Explicit logout intent confirmed - bypassing all protection');

                // Clear all protection flags when user explicitly logs out
                setDisconnectProtectionActive(false);
                localStorage.removeItem('wallet_disconnect_in_progress');
                sessionStorage.removeItem('wallet_switching');

                // Proceed directly to logout
            } else {
                // No explicit intent - apply all protection checks

                // 🔒 PROTECTION #1: Check pre-emptive protection (for Coinbase page reloads)
                if (disconnectProtectionActive) {
                    logger.log('🛡️ PRE-EMPTIVE PROTECTION: Active protection detected - Auth0 logout BLOCKED');
                    return; // Don't logout while protection is active
                }

                // 🔒 PROTECTION #2: Check if we're just switching wallets (sessionStorage flag)
                const isWalletSwitch = sessionStorage.getItem('wallet_switching') === 'true';

                if (isWalletSwitch) {
                    logger.log('🔄 Wallet switch detected (sessionStorage) - Auth0 logout prevented');
                    sessionStorage.removeItem('wallet_switching');
                    return; // Don't logout, just return
                }

                // 🔒 PROTECTION #3: Check if a wallet disconnect just happened (localStorage flag)
                // This catches Coinbase Wallet's aggressive disconnect behavior
                const disconnectTimestamp = localStorage.getItem('wallet_disconnect_in_progress');

                if (disconnectTimestamp) {
                    const disconnectTime = parseInt(disconnectTimestamp, 10);
                    const now = Date.now();
                    const timeSinceDisconnect = now - disconnectTime;

                    // If disconnect happened within last 3 seconds, it's a wallet change, not a logout
                    if (timeSinceDisconnect < 3000) {
                        logger.log(`🔌 Wallet disconnect detected (${timeSinceDisconnect}ms ago) - Auth0 logout prevented`);
                        return; // Don't logout, just return
                    } else {
                        // Old flag, clean it up
                        localStorage.removeItem('wallet_disconnect_in_progress');
                    }
                }

                // 🔒 PROTECTION #4: Block any logout without explicit intent
                logger.log('🚫 Logout called without explicit intent - BLOCKED (prevents Coinbase Wallet cascade)');
                return; // Don't logout without explicit intent
            }

            // 🔒 If we get here, it's an EXPLICIT logout request (user clicked logout button)
            logger.log('🔓 Proceeding with Auth0 logout');

            // Use the current origin for logout redirect
            const currentOrigin = window.location.origin;
            const logoutUrl = `${currentOrigin}/`;

            logger.log(`Logging out with federated session clearing to: ${logoutUrl}`);

            // Clear any application-level session data
            sessionStorage.clear();
            localStorage.removeItem('app_session_id');
            localStorage.removeItem('wallet_disconnect_in_progress'); // Clean up any stale flags

            // Call Auth0's logout with federated session clearing
            auth0Logout({
                logoutParams: {
                    returnTo: logoutUrl,
                    federated: true  // Clear all federated sessions
                }
            });
        } catch (error) {
            logger.error('Error during logout:', error);
            // Fallback to default logout if there's an error (but still require intent)
            if (explicitIntent) {
                auth0Logout();
            }
        }
    }, [auth0Logout]);

    const contextValue = {
        isAuthenticated,
        isLoading,
        user,
        loginWithRedirect,
        logout,
        getAccessTokenSilently,
        error
    };

    return (
        <Auth0Context.Provider value={contextValue}>
            {children}
        </Auth0Context.Provider>
    );
};

export const useAuth0 = () => {
    const context = useContext(Auth0Context);
    if (context === undefined) {
        throw new Error('useAuth0 must be used within an Auth0Provider');
    }
    return context;
};
